const FIRST_CHARACTER_PATTERN = /^./

export const COLLECTION_INVENTORY_ID = '18000000-0000-4000-8000-000000000001'
// Editor-only lookups reuse the collection preview so a picked game resolves
// through the same tenant runtime, regulation and normalization as the preview.
export const COLLECTION_EVENT_LOOKUP_ID = '18000000-0000-4000-8000-000000000002'
export const COLLECTION_EVENT_SEARCH_ID = '18000000-0000-4000-8000-000000000003'
export const collectionSport = (value) => ({ FOOTBALL: 'soccer', SOCCER: 'soccer', MLS: 'soccer', NFL: 'american_football', NCAAF: 'american_football', CFL: 'american_football', AMERICAN_FOOTBALL: 'american_football', NBA: 'basketball', WNBA: 'basketball', NCAAB: 'basketball', NHL: 'ice_hockey', HOCKEY: 'ice_hockey', MLB: 'baseball' }[String(value).toUpperCase()] || String(value).toLowerCase().replaceAll(' ', '_'))
export const collectionSportLabel = (value) => ({ american_football: 'Football', soccer: 'Soccer', basketball: 'Basketball', ice_hockey: 'Hockey', baseball: 'Baseball', tennis: 'Tennis' }[value] || value.replaceAll('_', ' ').replace(FIRST_CHARACTER_PATTERN, (letter) => letter.toUpperCase()))
export function newCollection(component = {}, id = crypto.randomUUID()) {
  return { schema_version: '1.1.0', id, kind: 'inventory', sports: [...new Set((component.sport_ids ?? []).map(collectionSport))], competitions: [], team_ids: [], event_ids: component.event_ids ?? [], query: null, state: 'active', order: 'start_time', following: 'all', pinned_event_ids: [], excluded_event_ids: [], limit: 6, empty_behavior: 'message', unavailable_behavior: 'cached' }
}
export const COLLECTION_REASONS = {
  preferences_first: 'Following first',
  followed_team: 'Follows a team in this game',
  followed_competition: 'Follows this competition',
  history_team: 'You bet on a team in this game before.',
  local_team: 'A local team plays in this game.',
  local_competition: 'A local competition.',
  default_order: 'Other matches ordered by start time',
  not_followed_team: 'No followed team',
  not_followed_competition: 'Competition not followed',
  not_followed: 'No followed team or competition',
  not_local: 'Not local to you.',
  audience_not_member: 'Not offered to this account.',
  audience_requires_account: 'Sign in to see this collection.',
  audience_unavailable: 'This collection is temporarily unavailable.',
  scope_match: 'Matches this collection',
  start_time: 'Soonest start first',
  live_first: 'Live games first',
  relevance: 'Search relevance',
  pinned: 'Pinned by you',
  event_unavailable: 'Game ended or unavailable',
  excluded_by_operator: 'Excluded by you',
  sport_mismatch: 'Different sport',
  competition_mismatch: 'Different competition',
  event_mismatch: 'Not one of the chosen games',
  team_mismatch: 'Different team',
  not_live: 'Not live',
  not_upcoming: 'Not upcoming',
  result_limit: 'Beyond the display limit',
  not_in_source_scope: 'Outside the retrieved inventory',
}
const foldIncludes = (values, value) => values.some((item) => item.toLowerCase() === String(value).toLowerCase())
function exclusion(definition, event, now) {
  if (['CLOSED', 'SETTLED', 'FINISHED', 'CANCELLED', 'CANCELED', 'POSTPONED', 'ABANDONED'].includes(event.status?.toUpperCase())) return 'event_unavailable'
  if (definition.excluded_event_ids.includes(event.id)) return 'excluded_by_operator'
  if (definition.sports.length && !foldIncludes(definition.sports, event.sport_family || collectionSport(event.sport))) return 'sport_mismatch'
  if (definition.competitions.length && !foldIncludes(definition.competitions, event.league)) return 'competition_mismatch'
  if (definition.event_ids.length && !definition.event_ids.includes(event.id)) return 'event_mismatch'
  if (definition.team_ids.length && !(event.participants ?? []).some((team) => definition.team_ids.includes(team.id))) return 'team_mismatch'
  if (definition.state === 'live' && !event.live) return 'not_live'
  if (definition.state === 'upcoming' && (event.live || Date.parse(event.start_time) <= now.getTime())) return 'not_upcoming'
  return null
}
export function collectionChoices(events) {
  return {
    sports: [...new Set(events.map((event) => event.sport_family || collectionSport(event.sport)))].sort(),
    competitions: [...new Set(events.map((event) => event.league))].filter(Boolean).sort(),
    competition_scopes: [...new Map(events.filter((event) => event.league).map((event) => {
      const sport = event.sport_family || collectionSport(event.sport)
      return [`${sport}:${event.league}`, { sport, id: event.league, name: event.league }]
    })).values()],
    teams: [...new Map(events.flatMap((event) => (event.participants ?? []).map((team) => ({ sport: event.sport_family || collectionSport(event.sport), id: team.id, name: team.name }))).map((team) => [`${team.sport}:${team.id}`, team])).values()].sort((a, b) => a.name.localeCompare(b.name)),
    events: events.map((event) => ({ id: event.id, name: event.name })),
  }
}
// Explicit sample source. Live resolution always comes from the authenticated API.
export function resolveSampleCollection(definition, catalog, now = new Date(), persona = null, regulation = null) {
  const scope = catalog.events.filter((event) => (!definition.sports.length || foldIncludes(definition.sports, event.sport_family || collectionSport(event.sport))) && (!definition.competitions.length || foldIncludes(definition.competitions, event.league)) && (definition.kind !== 'search' || `${event.name} ${event.league}`.toLowerCase().includes(definition.query?.toLowerCase())))
  const unique = [...new Map(scope.map((event) => [event.id, event])).values()]

  const audienceReason = audienceExclusion(definition, persona)
  const reasons = audienceReason ? [audienceReason] : []

  const explanations = new Map()
  const matches = new Map()
  const candidates = []

  for (const event of unique) {
    if (audienceReason) {
      explanations.set(event.id, [audienceReason])
      continue
    }
    const code = exclusion(definition, event, now)
    if (code) {
      explanations.set(event.id, [code])
      continue
    }
    const eventMatches = collectionFollowingCodes(event, persona, definition, regulation)
    matches.set(event.id, eventMatches)
    const followingCode = collectionFollowingExclusion(definition, eventMatches)
    if (followingCode) {
      explanations.set(event.id, [followingCode])
      continue
    }
    candidates.push(event)
    const codes = ['scope_match', definition.order]
    if (definition.order === 'preferences_first' || definition.following !== 'all') {
      codes.push(...eventMatches)
      if (definition.order === 'preferences_first' && !eventMatches.length) {
        codes.push('default_order')
      }
    }
    explanations.set(event.id, codes)
  }

  candidates.sort((a, b) => {
    const ai = definition.pinned_event_ids.indexOf(a.id), bi = definition.pinned_event_ids.indexOf(b.id)
    if (ai >= 0 || bi >= 0) return ai < 0 ? 1 : bi < 0 ? -1 : ai - bi
    if (definition.order === 'relevance') return 0
    if (definition.order === 'preferences_first') {
      const as = preferenceScore(matches.get(a.id) || [])
      const bs = preferenceScore(matches.get(b.id) || [])
      if (as !== bs) return bs - as
    }
    if (definition.order === 'live_first' && a.live !== b.live) return a.live ? -1 : 1
    return Date.parse(a.start_time) - Date.parse(b.start_time) || (a.id < b.id ? -1 : 1)
  })

  const events = []
  for (const [index, event] of candidates.entries()) {
    if (index >= definition.limit) {
      explanations.set(event.id, ['result_limit'])
      continue
    }
    const codes = [...(explanations.get(event.id) || [])]
    if (definition.pinned_event_ids.includes(event.id)) {
      codes.push('pinned')
    }
    explanations.set(event.id, codes.slice(0, 8))
    events.push(event)
  }
  for (const id of definition.pinned_event_ids) {
    if (!explanations.has(id)) {
      explanations.set(id, ['not_in_source_scope'])
    }
  }

  const state = events.length ? 'ready' : 'empty'

  return {
    schema_version: '1.1.0',
    collection_id: definition.id,
    state,
    complete: true,
    source: 'fixture',
    loaded_at: now.toISOString(),
    scanned: unique.length,
    matched: candidates.length,
    reasons,
    events,
    choices: collectionChoices(unique),
    decision: {
      policy_version: 'collections-v2',
      inputs: {
        schema_version: '1.0.0',
        source: persona ? 'preview' : 'anonymous',
        preferences_revision: null,
        persona_id: persona?.id || null,
      },
      explanations: [...explanations].map(([event_id, codes]) => ({ event_id, codes })),
    },
  }
}

function localRule(definition, jurisdiction, anonymous) {
  if (!definition?.local) return null
  if (anonymous) jurisdiction = definition.local.default_jurisdiction
  if (!jurisdiction) return null
  return (definition.local.rules || []).find((rule) => rule.jurisdiction === jurisdiction) || null
}

function audienceExclusion(definition, persona) {
  if (!definition?.audiences?.length) return ''
  if (!persona) return 'audience_requires_account'
  const memberships = new Set((persona.audiences || []).map((id) => `ims:${id}`))
  for (const ref of definition.audiences) {
    if (ref.provider === 'ims' && memberships.has(`ims:${ref.id}`)) return ''
  }
  return 'audience_not_member'
}

function matchesTeam(references, family, event) {
  if (!references?.length) return false
  return references.some((reference) => reference.sport === family && (event.participants || []).some((team) => team.id === reference.id))
}

function matchesCompetition(references, family, event) {
  if (!references?.length) return false
  return references.some((reference) => reference.sport === family && String(event.league || '').toLowerCase() === String(reference.id || '').toLowerCase())
}
// The native apps prefer the audience gate's reason over the generic state text.
export const collectionStateText = (result) => {
  const audience = (result?.reasons || []).find((reason) => reason.startsWith('audience_'))
  if (audience && COLLECTION_REASONS[audience]) return COLLECTION_REASONS[audience]
  return { loading: 'Loading games…', empty: 'No matching games', unavailable: 'Games are unavailable', incomplete: 'Some inventory is unavailable', cached: 'Showing recently loaded games. Prices are unavailable until we reconnect.' }[result?.state ?? 'loading'] || ''
}
// A transport outage can use only a complete recent result from this exact query key.
export function collectionPreviewFailure(definition, previous, now = Date.now()) {
  const loaded = Date.parse(previous?.loaded_at)
  if (definition.unavailable_behavior === 'cached' && previous?.complete && ['ready', 'empty', 'cached'].includes(previous.state) &&
      Number.isFinite(loaded) && loaded <= now && now - loaded <= 120_000) return { ...previous, state: 'cached' }
  return { state: 'unavailable', complete: false, events: [], choices: {} }
}

export function collectionFollowingCodes(event, persona, definition = {}, regulation = null) {
  const family = event.sport_family || collectionSport(event.sport)
  const codes = []
  const personal = Boolean(persona)
  if (personal) {
    if (matchesTeam(persona.teams, family, event)) codes.push('followed_team')
    if (matchesCompetition(persona.competitions, family, event)) codes.push('followed_competition')
    if (definition.interests === 'declared_and_history' && !codes.includes('followed_team') && matchesTeam(persona.history_teams, family, event)) {
      codes.push('history_team')
    }
  }
  const rule = localRule(definition, regulation, !personal)
  if (rule) {
    if (matchesTeam(rule.teams, family, event)) codes.push('local_team')
    if (matchesCompetition(rule.competitions, family, event)) codes.push('local_competition')
  }
  return codes
}
function collectionFollowingExclusion(definition, matches) {
  const teamInterest = matches.includes('followed_team') || matches.includes('history_team')
  switch (definition.following) {
    case 'teams':
      if (!teamInterest) return 'not_followed_team'
      break
    case 'competitions':
      if (!matches.includes('followed_competition')) return 'not_followed_competition'
      break
    case 'any':
      if (!teamInterest && !matches.includes('followed_competition')) return 'not_followed'
      break
    case 'local':
      if (!matches.includes('local_team') && !matches.includes('local_competition')) return 'not_local'
      break
  }
  return null
}
const preferenceScore = (codes) => codes.length + (codes.includes('followed_team') ? 2 : 0) + (codes.includes('history_team') || codes.includes('local_team') ? 1 : 0)

// Named synthetic choices come from available inventory; none writes to an account.
export function collectionPersonas(catalog) {
  // Source choices include identities beyond the display limit. Do not infer
  // available personas from the first twenty displayed games.
  const choices = catalog.choices || collectionChoices(catalog.events)
  const profiles = [
    ...(choices.teams || []).map((team) => ({ name: `${team.name} follower · ${collectionSportLabel(team.sport)}`, teams: [team], competitions: [], history_teams: [], audiences: [] })),
    ...(choices.competition_scopes || []).map((competition) => ({ name: `${competition.name} follower`, teams: [], competitions: [competition], history_teams: [], audiences: [] })),
  ]
  return [{ name: 'Signed in, follows nobody', teams: [], competitions: [], history_teams: [], audiences: [] }, ...profiles].map((profile, index) => ({ schema_version: '1.0.0', id: `19000000-0000-4000-8000-${String(index + 1).padStart(12, '0')}`, ...profile, name: profile.name.slice(0, 100) }))
}
